import { useState, useCallback, useMemo, ChangeEvent } from 'react';
import { pickBy } from 'lodash';
import { addSettlement, updateSettlement } from 'api/settlements';
import showNotification from 'components/ui/notification/showNotification';
import { changeDataForceUpdate } from 'actions/getData';
import tableNames from 'constants/tableNames';
import { StoreProps } from 'store';
import { Handlers, SelectInterface, SettlementData } from 'pages/settlement/types';
import { IntlProps } from 'decorators/addTranslation';
import { closeModal } from 'actions/modal';
import SelectItem from 'types/SelectItem';

interface Props extends StoreProps, IntlProps {
  isNew: boolean;
  content: SettlementData,
  currencies: SelectInterface;
  statuses: SelectInterface;
  userMerchant: SelectInterface;
  settlementBalanceUser: SelectInterface;
  twoFactorAuth: boolean;
}

interface ResultInterface {
  handlers: Handlers;
  isLoading: boolean;
  validationErrors: Record<string, string>,
  formData: SettlementData;
  saveSection: () => {}
}

export const useSettlementForm = (props: Props): ResultInterface => {
  const { content, isNew, currencies, statuses, settlementBalanceUser, dispatch, userMerchant, twoFactorAuth } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<SettlementData>({
    walletAddress: content?.walletAddress || '',
    amount: content?.amount || '',
    requestAmount: content?.requestAmount || '',
    status: statuses.list.find(({ value }: any) => value === content?.status) || '',
    currency: currencies.list.find(({ value }: any) => value === content?.currency) || '',
    comment: content?.comment || '',
    fee: content?.fee || '',
    rate: content?.rate || '',
    transactionHash: content?.transactionHash || '',
    completedDate: content?.completedDate || '',
    merchantId: userMerchant.list.find(({ value }: any) => value === content?.merchantId) || '',
    balanceId: settlementBalanceUser.list.find(({ value }: any) => value === content?.balanceId) || '',
  });

  const createChangeHandler = useCallback((field: string) =>
    (value?: ChangeEvent<SelectItem>) => {
      const newValue = value?.target?.value !== undefined ? value.target.value : value;
      setFormData(prev => ({ ...prev, [field]: newValue }));
    }, []);

  const handlers: Handlers = useMemo(() => {
     return {
       walletAddress: createChangeHandler('walletAddress'),
       amount: createChangeHandler('amount'),
       requestAmount: createChangeHandler('requestAmount'),
       status: createChangeHandler('status'),
       currency: createChangeHandler('currency'),
       comment: createChangeHandler('comment'),
       fee: createChangeHandler('fee'),
       rate: createChangeHandler('rate'),
       transactionHash: createChangeHandler('transactionHash'),
       completedDate: createChangeHandler('completedDate'),
       merchantId: createChangeHandler('merchantId'),
       balanceId: createChangeHandler('balanceId'),
     }
  }, [createChangeHandler]);

  const saveSection = useCallback(async () => {
    const getValue = (field?: SelectItem | string | number) => typeof field === 'object' ? field?.value : field;
    const sanitizeNumber = (value: string | number | undefined) => value
      ? String(value).replace(/\s/g, '')
      : value;

    const baseParams = {
      walletAddress: formData.walletAddress,
      amount: sanitizeNumber(formData.amount),
      currency: getValue(formData.currency),
      comment: formData.comment,
      balanceId: getValue(formData.balanceId),
    };

    const options = pickBy(
      isNew ? baseParams : {
        ...baseParams,
        id: content.id,
        requestAmount: sanitizeNumber(formData.requestAmount),
        status: getValue(formData.status),
        fee: sanitizeNumber(formData.fee),
        rate: sanitizeNumber(formData.rate),
        transactionHash: getValue(formData.transactionHash),
        completedDate: formData.completedDate,
        merchantId: getValue(formData.merchantId),
      },
    );

    setIsLoading(true);

    const action = isNew ? addSettlement : updateSettlement;

    try {
      await action(options);

      setValidationErrors({});

      if (!twoFactorAuth) {
        dispatch(closeModal());
        await dispatch(changeDataForceUpdate(tableNames.settlements, true));
      }

      if (formData.walletAddress === props.content.walletAddress) {
        dispatch(closeModal());
        await dispatch(changeDataForceUpdate(tableNames.settlements, true));
      }
    } catch (error: any) {
      if (error.payload?.validationErrors) {
        setValidationErrors(error.payload.validationErrors);
      } else {
        showNotification({ status: 'error', content: error.payload });
      }
    } finally {
      setIsLoading(false);
    }
  }, [isNew, formData, content, dispatch, twoFactorAuth, props]);

  return {
    isLoading,
    validationErrors,
    formData,
    handlers,
    saveSection,
  };
};